import { useState } from "react";
import { Link } from "react-router-dom";
import { RetrospectiveSummaryDTO } from "@retrospecter/shared/types";
import { useRetroList, RetroFilter } from "../hooks/useRetroList";
import { useTranslation } from "../i18n";

const FILTERS: { id: RetroFilter; labelKey: string }[] = [
  { id: "ALL", labelKey: "list.filterAll" },
  { id: "COMPLETED", labelKey: "list.filterPast" },
  { id: "ACTIVE", labelKey: "list.filterRunning" },
  { id: "DRAFT", labelKey: "list.filterDrafts" },
];

/** Lifecycle state → translation key. */
const STATUS_KEYS: Record<string, string> = {
  DRAFT: "board.statusDraft",
  ACTIVE: "board.statusActive",
  COMPLETED: "board.statusCompleted",
};

const STATUS_STYLES: Record<string, string> = {
  DRAFT: "bg-amber-500/15 text-amber-600 dark:text-amber-300",
  ACTIVE: "bg-emerald-500/15 text-emerald-600 dark:text-emerald-300",
  COMPLETED: "bg-ink/10 text-ink-muted",
};

function RetroRow({ retro }: { retro: RetrospectiveSummaryDTO }) {
  const { t, plural, formatDate, templateName } = useTranslation();

  const dateLabel = retro.completedAt
    ? t("list.completedOn", { date: formatDate(retro.completedAt) })
    : t("list.createdOn", { date: formatDate(retro.createdAt) });

  return (
    <li>
      <Link
        to={`/r/${retro.publicId}`}
        className="block rounded-xl border border-line bg-surface p-4 shadow-subtle transition-all duration-200 hover:-translate-y-0.5 hover:border-nm-accent/40 hover:shadow-raised"
      >
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div className="min-w-0">
            <h3 className="mb-1 truncate font-semibold text-ink">{retro.name}</h3>
            <p className="text-sm text-ink-muted">
              {templateName(retro.templateName)} · {dateLabel}
            </p>
          </div>

          <span
            className={`rounded-full px-2.5 py-0.5 text-xs font-semibold ${STATUS_STYLES[retro.status]}`}
          >
            {t(STATUS_KEYS[retro.status] ?? "board.statusDraft")}
          </span>
        </div>

        <div className="mt-3 flex flex-wrap items-center gap-4 text-xs text-ink-faint">
          <span>{plural("list.notes", retro.noteCount)}</span>
          <span>{plural("list.participants", retro.participantCount)}</span>
          {retro.isOwner ? (
            <span className="font-semibold text-nm-accent">{t("common.yours")}</span>
          ) : (
            <span>{t("common.by", { name: retro.ownerStaffUsername })}</span>
          )}
        </div>
      </Link>
    </li>
  );
}

export function RetroList() {
  const { t } = useTranslation();
  const [filter, setFilter] = useState<RetroFilter>("ALL");
  const { retrospectives, loading, refreshing, error } = useRetroList(filter);

  return (
    <section aria-label={t("list.regionLabel")} className="mt-12">
      <div className="mb-5 flex flex-wrap items-center justify-between gap-4">
        <h2 className="text-[0.7rem] font-bold uppercase tracking-[0.08em] text-ink-muted">
          {t("list.heading")}
        </h2>

        <div
          role="tablist"
          aria-label={t("list.filterLabel")}
          className="flex gap-1 rounded-full bg-surface-sunken p-1"
        >
          {FILTERS.map((option) => {
            const selected = option.id === filter;
            return (
              <button
                key={option.id}
                role="tab"
                aria-selected={selected}
                onClick={() => setFilter(option.id)}
                className={`rounded-full px-3 py-1 text-sm font-semibold transition-all duration-150 ${
                  selected
                    ? "bg-surface text-ink shadow-subtle"
                    : "text-ink-muted hover:text-ink"
                }`}
              >
                {t(option.labelKey)}
              </button>
            );
          })}
        </div>
      </div>

      {loading && (
        <ul className="space-y-3" aria-hidden="true">
          {[0, 1, 2].map((row) => (
            <li
              key={row}
              className="h-[5.5rem] animate-pulse rounded-xl border border-line bg-surface-sunken"
            />
          ))}
        </ul>
      )}

      {error && !loading && (
        <div className="rounded-xl border border-red-500/30 bg-red-500/10 p-5">
          <p className="text-sm text-red-500">
            {t("list.loadError")}
          </p>
        </div>
      )}

      {!loading && !error && retrospectives.length === 0 && (
        <div className="rounded-xl border border-dashed border-line-strong p-10 text-center">
          <div className="mb-2 text-3xl" aria-hidden="true">
            🗒️
          </div>
          <p className="text-sm text-ink-muted">
            {filter === "ALL" ? t("list.empty") : t("list.emptyFiltered")}
          </p>
        </div>
      )}

      {!loading && !error && retrospectives.length > 0 && (
        <ul
          className={`space-y-3 transition-opacity ${refreshing ? "opacity-60" : ""}`}
          aria-busy={refreshing}
        >
          {retrospectives.map((retro) => (
            <RetroRow key={retro.publicId} retro={retro} />
          ))}
        </ul>
      )}
    </section>
  );
}
